import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { ArrowUpDown } from 'lucide-react'
import Gravatar from 'react-gravatar'
import { formatCurrency, getTimeData } from '@/lib/utils'
import { GetOrgServerAnalyticsDataItem } from '@/services/inference.gen'

// Define AnalyticsData type based on the generated API types
type AnalyticsData = GetOrgServerAnalyticsDataItem

type AnalyticsTableProps = {
  data: AnalyticsData[]
}

export function AnalyticsUsersTable({ data }: AnalyticsTableProps) {
  const [sortBy, setSortBy] = useState<string>('duration_seconds')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(columnId)
      setSortDirection('desc')
    }
  }

  const sortedData = [...data].sort((a, b) => {
    const valueA = a[sortBy as keyof AnalyticsData]
    const valueB = b[sortBy as keyof AnalyticsData]

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA
    }

    return 0
  })

  if (!sortedData.length) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-muted-foreground">
          No usage data available for this time period
        </p>
      </div>
    )
  }

  return (
    <div className="w-full rounded-t-lg border shadow-sm overflow-hidden bg-card border-b-0">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="w-[45%] font-bold">Users List</TableHead>
            <TableHead className="w-[27.5%]">
              <Button
                variant="link"
                className="px-0 text-foreground font-bold"
                onClick={() => handleSort('duration_seconds')}
              >
                Time Spent
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead className="w-[27.5%]">
              <Button
                variant="link"
                className="px-0 text-foreground font-bold"
                onClick={() => handleSort('spent')}
              >
                $ Spent
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((item) => {
            const { hours, minutes, seconds } = getTimeData(
              item.duration_seconds
            )
            const formatted = formatCurrency(item.spent / 100)

            return (
              <TableRow key={item.user_id} className="hover:bg-muted/20">
                <TableCell className="py-4">
                  <div className="flex items-center gap-3 ml-3">
                    {item.profile_metadata?.avatar_preview_url ? (
                      <img
                        src={item.profile_metadata.avatar_preview_url}
                        alt={`${item.profile_metadata?.first_name || ''} ${item.profile_metadata?.last_name || ''}'s avatar`}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <Gravatar
                        email={item.email}
                        className="h-10 w-10 rounded-full"
                      />
                    )}

                    <div>
                      <div className="font-medium">
                        {item?.profile_metadata?.first_name || '-'}{' '}
                        {item?.profile_metadata?.last_name || '-'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {item.email}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-4">
                  {hours}h {minutes}m {seconds}s
                </TableCell>
                <TableCell className="py-4 font-medium">{formatted}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
